
import React from 'react';
import { Table, Button, Dropdown, message } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FileTextOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TableComponent = ({
    columns,
    dataSource,
    onChange,
    pagination: externalPagination,
    loading,
    exportFileName = 'export',
    exportDataSource,
    toolbarContent,
    showExport = true,
    ...tableProps
}) => {
    // Filtrer les colonnes visibles (qui n'ont pas hideInTable)
    const visibleColumns = columns.filter(col => !col.hideInTable);

    // Filtrer les colonnes exportables (qui n'ont pas hideInExport)
    const exportableColumns = columns.filter(col => !col.hideInExport);

    // Jeu de données utilisé pour l'export (peut être différent du dataSource affiché)
    const dataForExport = exportDataSource || dataSource;

    // Extraire la valeur brute pour l'export (ignorer les render)
    const extractValue = (row, col) => {
        if (col.exportValue) {
            return col.exportValue(row);
        }
        return row[col.dataIndex] || '';
    };

    // Export en CSV
    const exportToCSV = () => {
        try {
            const headers = exportableColumns.map(col => col.title).join(',');
            const rows = dataForExport.map(row =>
                exportableColumns.map(col => {
                    const value = extractValue(row, col);
                    // Échapper les valeurs qui contiennent des virgules ou des guillemets
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                        return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                }).join(',')
            );

            const csv = [headers, ...rows].join('\n');
            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            downloadFile(blob, `${exportFileName}.csv`);
            message.success('Export CSV réussi');
        } catch (error) {
            message.error('Erreur lors de l\'export CSV');
            console.error(error);
        }
    };

    // Export en Excel
    const exportToExcel = () => {
        try {
            const data = dataForExport.map(row =>
                exportableColumns.reduce((acc, col) => {
                    acc[col.title] = extractValue(row, col);
                    return acc;
                }, {})
            );

            const worksheet = XLSX.utils.json_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Export');

            // Ajuster la largeur des colonnes
            const maxWidth = exportableColumns.map(col => {
                const headerLength = col.title.length;
                const dataLengths = dataForExport.map(row =>
                    String(extractValue(row, col)).length
                );
                return Math.max(headerLength, ...dataLengths, 10);
            });

            worksheet['!cols'] = maxWidth.map(w => ({ wch: Math.min(w + 2, 50) }));

            XLSX.writeFile(workbook, `${exportFileName}.xlsx`);
            message.success('Export Excel réussi');
        } catch (error) {
            message.error('Erreur lors de l\'export Excel');
            console.error(error);
        }
    };

    // Export en PDF
    const exportToPDF = () => {
        try {
            const doc = new jsPDF();
            const headers = exportableColumns.map(col => col.title);
            const body = dataForExport.map(row =>
                exportableColumns.map(col => extractValue(row, col))
            );

            autoTable(doc, {
                head: [headers],
                body,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [66, 133, 244] },
            });

            doc.save(`${exportFileName}.pdf`);
            message.success('Export PDF réussi');
        } catch (error) {
            message.error('Erreur lors de l\'export PDF');
            console.error(error);
        }
    };

    // Fonction utilitaire pour télécharger un fichier
    const downloadFile = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // Menu items pour le dropdown
    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Export CSV',
            icon: <FileTextOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Export Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        },
        {
            key: 'pdf',
            label: 'Export PDF',
            icon: <FileTextOutlined />,
            onClick: exportToPDF
        }
    ];

    const renderExportButton = () => (
        <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button icon={<DownloadOutlined />}>
                Exporter
            </Button>
        </Dropdown>
    );

    return (
        <div>
            {(toolbarContent || showExport) && (
                <div style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    flexWrap: 'wrap',
                }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {toolbarContent}
                    </div>
                    {showExport && renderExportButton()}
                </div>
            )}

            <Table
                columns={visibleColumns}
                dataSource={dataSource}
                onChange={onChange}
                pagination={externalPagination || {
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `${total} éléments`,
                }}
                loading={loading}
                scroll={{ x: true }}
                style={{ padding: '0 4px' }}
                {...tableProps}
            />
        </div>
    );
};
export default TableComponent;
